import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FileText, Image as ImageIcon, GripVertical, Plus, Video, Circle } from 'lucide-react';
import { Lesson } from '../../types';
import { OutlineSelection } from './types';

interface OutlineNavProps {
  selection: OutlineSelection;
  onSelect: (sel: OutlineSelection) => void;
  lessons: Lesson[];
  galleryCount: number;
  basicsDirty: boolean;
  galleryDirty: boolean;
  dirtyLessonIds: Set<string>;
  onReorder: (lessons: Lesson[]) => void;
  onAddLesson: () => void;
}

const rowBase = 'w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors text-left';

function UnsavedDot() {
  return <Circle className="w-2 h-2 fill-amber-500 text-amber-500 flex-shrink-0" />;
}

function SortableLesson({ lesson, index, active, dirty, onSelect }: { lesson: Lesson; index: number; active: boolean; dirty: boolean; onSelect: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lesson._id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className={`group flex items-center rounded-md ${active ? 'bg-gold/10' : 'hover:bg-gray-100'}`}>
      <button {...attributes} {...listeners} className="pl-2 py-2 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing touch-none" tabIndex={-1}>
        <GripVertical className="w-4 h-4" />
      </button>
      <button type="button" onClick={onSelect} className={`flex-1 min-w-0 flex items-center gap-2 pr-2 py-2 text-sm text-left ${active ? 'text-gold font-medium' : 'text-gray-700'}`}>
        <span className="text-gray-400 w-4 flex-shrink-0">{index + 1}</span>
        <span className="flex-1 min-w-0 truncate">{lesson.title || '—'}</span>
        {lesson.videoUrl || lesson.videoId ? <Video className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /> : null}
        {dirty && <UnsavedDot />}
      </button>
    </div>
  );
}

export default function OutlineNav({ selection, onSelect, lessons, galleryCount, basicsDirty, galleryDirty, dirtyLessonIds, onReorder, onAddLesson }: OutlineNavProps) {
  const { t } = useTranslation('teaching');
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = lessons.findIndex((l) => l._id === active.id);
    const newIndex = lessons.findIndex((l) => l._id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(lessons, oldIndex, newIndex));
  }

  return (
    <nav className="glass-card rounded-lg p-2 space-y-1">
      <button type="button" onClick={() => onSelect({ kind: 'basics' })} className={`${rowBase} ${selection.kind === 'basics' ? 'bg-gold/10 text-gold font-medium' : 'text-gray-700 hover:bg-gray-100'}`}>
        <FileText className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1">{t('courseEdit.outlineBasics')}</span>
        {basicsDirty && <UnsavedDot />}
      </button>

      <button type="button" onClick={() => onSelect({ kind: 'media' })} className={`${rowBase} ${selection.kind === 'media' ? 'bg-gold/10 text-gold font-medium' : 'text-gray-700 hover:bg-gray-100'}`}>
        <ImageIcon className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1">{t('courseEdit.outlineMedia')}</span>
        {galleryCount > 0 && <span className="text-xs text-gray-400">{galleryCount}</span>}
        {galleryDirty && <UnsavedDot />}
      </button>

      <div className="pt-2 pb-1 px-3 flex items-center justify-between">
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{t('courseEdit.outlineLessons')}</span>
        <span className="text-xs text-gray-400">{lessons.length}</span>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={lessons.map((l) => l._id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-0.5">
            {lessons.map((lesson, index) => (
              <SortableLesson
                key={lesson._id}
                lesson={lesson}
                index={index}
                active={selection.kind === 'lesson' && selection.id === lesson._id}
                dirty={dirtyLessonIds.has(lesson._id)}
                onSelect={() => onSelect({ kind: 'lesson', id: lesson._id })}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <button type="button" onClick={onAddLesson} className={`${rowBase} text-gold hover:bg-gold/10 mt-1`}>
        <Plus className="w-4 h-4 flex-shrink-0" />
        <span>{t('courseEdit.addLesson')}</span>
      </button>
    </nav>
  );
}
